// Estados visuales del ciclo borrador → publicar → pausar/vencer. Cada dominio traduce su status
// de backend a uno de estos; el nombre y el color salen siempre de aquí.
import type { PillTone } from '@/shared/patterns/Pills';

export type LifecycleStatus =
    | 'DRAFT'
    | 'SCHEDULED'
    | 'ACTIVE'
    | 'PERMANENT'
    | 'PAUSED'
    | 'EXPIRED'
    | 'ARCHIVED'
    | 'DELETED';

/** Estados en que el registro no aplica hoy: su nombre va atenuado en las listas. */
export const LIFECYCLE_DIMMED_STATUSES: ReadonlySet<LifecycleStatus> = new Set<LifecycleStatus>([
    'DRAFT',
    'PAUSED',
    'EXPIRED',
    'ARCHIVED',
]);

export function isDimmedLifecycleStatus(status: LifecycleStatus): boolean {
    return LIFECYCLE_DIMMED_STATUSES.has(status);
}

/** Etiquetas en femenino. */
export const LIFECYCLE_STATUS_LABEL: Record<LifecycleStatus, string> = {
    DRAFT: 'Borrador',
    SCHEDULED: 'Programada',
    ACTIVE: 'Activa',
    PERMANENT: 'Permanente',
    PAUSED: 'Pausada',
    EXPIRED: 'Vencida',
    ARCHIVED: 'Archivada',
    DELETED: 'Eliminada',
};

export const LIFECYCLE_STATUS_TONE: Record<LifecycleStatus, PillTone> = {
    DRAFT: 'neutral',
    SCHEDULED: 'info',
    ACTIVE: 'success',
    PERMANENT: 'success',
    PAUSED: 'warning',
    EXPIRED: 'neutral',
    ARCHIVED: 'neutral',
    DELETED: 'danger',
};

/** Arma el diccionario de etiquetas de un dominio a partir de su traducción a estado visual. */
export function lifecycleLabels<K extends string>(
    toLifecycle: Record<K, LifecycleStatus>,
): Record<K, string> {
    const labels = {} as Record<K, string>;
    for (const key of Object.keys(toLifecycle) as K[]) {
        labels[key] = LIFECYCLE_STATUS_LABEL[toLifecycle[key]];
    }
    return labels;
}

/** Eventos de una bitácora de ciclo de vida, con el mismo nombre en todas las pantallas. */
export type LifecycleEvent =
    | 'CREATE'
    | 'UPDATE'
    | 'PUBLISH'
    | 'PAUSE'
    | 'RESUME'
    | 'EXPIRE'
    | 'ARCHIVE'
    | 'DELETE';

export const LIFECYCLE_EVENT_LABEL: Record<LifecycleEvent, string> = {
    CREATE: 'Creación',
    UPDATE: 'Edición',
    PUBLISH: 'Publicación',
    PAUSE: 'Pausa',
    RESUME: 'Reanudación',
    EXPIRE: 'Vencimiento',
    ARCHIVE: 'Archivo',
    DELETE: 'Eliminación',
};

export const LIFECYCLE_EVENT_TONE: Record<LifecycleEvent, PillTone> = {
    CREATE: 'neutral',
    UPDATE: 'info',
    PUBLISH: 'success',
    PAUSE: 'warning',
    RESUME: 'info',
    EXPIRE: 'neutral',
    ARCHIVE: 'neutral',
    DELETE: 'danger',
};

const KNOWN_EVENTS = new Set<string>(Object.keys(LIFECYCLE_EVENT_LABEL));

/** Un evento que el backend agregue llega como string: se reconoce o se deja como desconocido. */
export function toLifecycleEvent(event: string): LifecycleEvent | null {
    return KNOWN_EVENTS.has(event) ? (event as LifecycleEvent) : null;
}
